/**
 * SettingsProfilePage — Edit user profile (name, phone, avatar)
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { Camera, Save, Loader2, ShieldCheck, Mic, Monitor, KeyRound, Palette } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api, apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { BooxCiMark } from '@/components/brand/BooxCiMark';
import { useAppStore } from '@/stores/app-store';
import {
  BOOX_CI_SET_OPTIONS,
  BOOX_ICON_SET_OPTIONS,
  type BooxCiSetId,
  type BooxIconSetId,
} from '@/lib/visual-preferences';

type MfaMethod = 'none' | 'totp' | 'whatsapp_otp' | 'email_otp';
type MultiLoginPolicy = 'none' | 'hidden' | 'notify';

interface TotpStartResponse {
  method: 'totp';
  secret: string;
  otpauthUrl: string;
  expiresAt: string;
}

function normalizeMfaMethod(method?: string | null): MfaMethod {
  if (method === 'totp' || method === 'email_otp' || method === 'whatsapp_otp') return method;
  if (method === 'email') return 'email_otp';
  if (method === 'whatsapp') return 'whatsapp_otp';
  return 'none';
}

export default function SettingsProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>('none');
  const [totpSetup, setTotpSetup] = useState<TotpStartResponse | null>(null);
  const [totpQrDataUrl, setTotpQrDataUrl] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpDisableCode, setTotpDisableCode] = useState('');
  const [totpBusy, setTotpBusy] = useState(false);
  const [voiceInputEnabled, setVoiceInputEnabled] = useState(false);
  const [multiLoginPolicy, setMultiLoginPolicy] = useState<MultiLoginPolicy>('hidden');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const { pwaCiSet, setPwaCiSet, pwaIconSet, setPwaIconSet } = useAppStore();

  // Load profile on mount
  useEffect(() => {
    apiFetch<{ displayName?: string; phone?: string; avatarUrl?: string; twofaMethod?: string; voiceInputEnabled?: boolean; multiLoginPolicy?: string }>('/auth/me/profile')
      .then((d) => {
        setDisplayName(d.displayName ?? '');
        setPhone(d.phone ?? '');
        setAvatarUrl(d.avatarUrl ?? '');
        setMfaMethod(normalizeMfaMethod(d.twofaMethod));
        setVoiceInputEnabled(d.voiceInputEnabled ?? false);
        setMultiLoginPolicy((d.multiLoginPolicy as MultiLoginPolicy) ?? 'hidden');
      })
      .catch(() => setError('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await apiFetch('/auth/me/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          displayName,
          phone,
          avatarUrl,
          twofaMethod: mfaMethod === 'totp' ? undefined : mfaMethod,
          voiceInputEnabled,
          multiLoginPolicy,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarFile = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setError('Avatar must be under 2 MB');
      return;
    }
    // Convert to base64 data URL for simplicity (can switch to R2 upload later)
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const initials = displayName
    ? displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : (user?.email?.[0] ?? 'U').toUpperCase();

  const startTotpSetup = async () => {
    setTotpBusy(true);
    setError('');
    try {
      const setup = await api.post<TotpStartResponse>('/auth/mfa/totp/start', {});
      setTotpSetup(setup);
      setTotpCode('');
      setTotpQrDataUrl(await QRCode.toDataURL(setup.otpauthUrl, { margin: 1, width: 180 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start authenticator setup');
    } finally {
      setTotpBusy(false);
    }
  };

  const confirmTotpSetup = async () => {
    if (!/^\d{6}$/.test(totpCode)) {
      setError('Enter the 6-digit authenticator code.');
      return;
    }
    setTotpBusy(true);
    setError('');
    try {
      await api.post('/auth/mfa/totp/confirm', { code: totpCode });
      setMfaMethod('totp');
      setTotpSetup(null);
      setTotpQrDataUrl('');
      setTotpCode('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to confirm authenticator code');
    } finally {
      setTotpBusy(false);
    }
  };

  const disableTotp = async () => {
    if (!/^\d{6}$/.test(totpDisableCode)) {
      setError('Enter the current 6-digit authenticator code to disable MFA.');
      return;
    }
    setTotpBusy(true);
    setError('');
    try {
      await api.post('/auth/mfa/totp/disable', { code: totpDisableCode });
      setMfaMethod('none');
      setTotpDisableCode('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to disable authenticator MFA');
    } finally {
      setTotpBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-lg py-12 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-4 py-6 px-4">
      <div className="pb-1">
        <h1 className="text-xl font-bold">{t('settings.profile', 'My Profile')}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t('settings.profileDesc', 'Update your personal information and avatar.')}
        </p>
      </div>

      {/* Avatar */}
      <section className="rounded-xl border bg-card p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          {t('settings.avatar', 'Avatar')}
        </h2>
        <div className="flex items-center gap-4">
          <button
            onClick={() => fileRef.current?.click()}
            className="relative group"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="h-20 w-20 rounded-full object-cover" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
                {initials}
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="h-6 w-6 text-white" />
            </div>
          </button>
          <div className="text-sm text-muted-foreground">
            <p>{t('settings.avatarHint', 'Click to upload. Max 2 MB.')}</p>
            {avatarUrl && (
              <button
                onClick={() => setAvatarUrl('')}
                className="text-destructive hover:underline text-xs mt-1"
              >
                {t('common.remove', 'Remove')}
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleAvatarFile(f);
              e.target.value = '';
            }}
          />
        </div>
      </section>

      {/* Name & Phone */}
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('settings.personalInfo', 'Personal Information')}
        </h2>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.displayName', 'Display Name')}</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="John Doe"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.email', 'Email')}</label>
          <input
            type="email"
            value={user?.email ?? ''}
            disabled
            className="w-full rounded-lg border border-input bg-muted px-3 py-2 text-sm text-muted-foreground cursor-not-allowed"
          />
          <p className="text-xs text-muted-foreground mt-0.5">{t('settings.emailReadonly', 'Email cannot be changed.')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('settings.phone', 'Phone')}</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="+65 9123 4567"
          />
        </div>
      </section>

      {/* MFA / Two-Factor Authentication */}
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" />
          {t('settings.mfa', 'Two-Factor Authentication')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('settings.mfaDesc', 'Add an extra layer of security to your account by requiring a verification code when signing in.')}
        </p>
        <div className="space-y-2">
          {([
            { value: 'none', label: t('settings.mfaNone', 'None'), desc: t('settings.mfaNoneDesc', 'No two-factor authentication') },
            { value: 'totp', label: 'Authenticator App', desc: 'Use Google Authenticator, Microsoft Authenticator, 1Password, or Authy.' },
            { value: 'whatsapp_otp', label: 'WhatsApp', desc: t('settings.mfaWhatsappDesc', 'Receive verification code via WhatsApp') },
            { value: 'email_otp', label: t('settings.mfaEmail', 'Email'), desc: t('settings.mfaEmailDesc', 'Receive verification code via email') },
          ] as const).map((opt) => (
            <label
              key={opt.value}
              className={cn(
                'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                mfaMethod === opt.value
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-input hover:bg-accent/50',
              )}
            >
              <input
                type="radio"
                name="mfa"
                value={opt.value}
                checked={mfaMethod === opt.value}
                onChange={() => {
                  if (opt.value === 'totp') {
                    void startTotpSetup();
                  } else {
                    setMfaMethod(opt.value);
                    setTotpSetup(null);
                    setTotpQrDataUrl('');
                  }
                }}
                className="accent-primary h-4 w-4"
              />
              <div>
                <div className="text-sm font-medium flex items-center gap-1.5">
                  {opt.value === 'totp' && <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />}
                  {opt.label}
                </div>
                <div className="text-xs text-muted-foreground">{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>

        {totpSetup && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-3">
            <div className="text-sm font-medium">Set up Authenticator App</div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              {totpQrDataUrl ? (
                <img
                  src={totpQrDataUrl}
                  alt="Authenticator setup QR code"
                  className="h-[180px] w-[180px] rounded-md border bg-white p-2"
                />
              ) : (
                <div className="flex h-[180px] w-[180px] items-center justify-center rounded-md border bg-background">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Scan this QR code, then enter the 6-digit code from your authenticator app.
                </p>
                <div className="rounded-md bg-background px-2 py-1 font-mono text-xs text-muted-foreground break-all">
                  {totpSetup.secret}
                </div>
                <div className="flex gap-2">
                  <input
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    className="w-32 rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="000000"
                  />
                  <button
                    type="button"
                    onClick={confirmTotpSetup}
                    disabled={totpBusy || totpCode.length !== 6}
                    className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {totpBusy ? 'Verifying...' : 'Enable'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {mfaMethod === 'totp' && !totpSetup && (
          <div className="rounded-lg border border-input p-3 space-y-2">
            <div className="text-sm font-medium">Authenticator App is enabled</div>
            <p className="text-xs text-muted-foreground">
              To disable it, enter the current 6-digit authenticator code.
            </p>
            <div className="flex gap-2">
              <input
                value={totpDisableCode}
                onChange={(e) => setTotpDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                className="w-32 rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="000000"
              />
              <button
                type="button"
                onClick={disableTotp}
                disabled={totpBusy || totpDisableCode.length !== 6}
                className="rounded-lg border border-destructive/30 px-3 py-2 text-sm font-medium text-destructive disabled:opacity-50"
              >
                Disable
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Visual Identity */}
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Palette className="h-3.5 w-3.5" />
          {t('settings.visualIdentity', 'Visual Identity')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('settings.visualIdentityDesc', 'Choose the CI mascot set and icon set used by the PWA shell.')}
        </p>

        <div className="space-y-2">
          <label className="block text-sm font-medium">{t('settings.ciSet', 'CI set')}</label>
          <div className="grid grid-cols-[56px_1fr] gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border bg-background">
              <BooxCiMark ciSet={pwaCiSet} className="h-11 w-11" />
            </div>
            <select
              value={pwaCiSet}
              onChange={(event) => setPwaCiSet(event.target.value as BooxCiSetId)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {BOOX_CI_SET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {BOOX_CI_SET_OPTIONS.find((option) => option.value === pwaCiSet)?.description}
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium">{t('settings.iconSet', 'Icon set')}</label>
          <div className="grid grid-cols-[56px_1fr] gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border bg-background">
              <Monitor className="h-6 w-6 text-muted-foreground" />
            </div>
            <select
              value={pwaIconSet}
              onChange={(event) => setPwaIconSet(event.target.value as BooxIconSetId)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {BOOX_ICON_SET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {BOOX_ICON_SET_OPTIONS.find((option) => option.value === pwaIconSet)?.description}
          </p>
        </div>
      </section>

      {/* Voice Input */}
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Mic className="h-3.5 w-3.5" />
          {t('settings.voiceInput', 'Voice Input')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('settings.voiceInputDesc', 'Enable voice-to-text input in chat. Hold the mic button to speak.')}
        </p>
        <label className={cn(
          'flex items-center justify-between rounded-lg border p-3 cursor-pointer transition-colors',
          voiceInputEnabled ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-input hover:bg-accent/50',
        )}>
          <div>
            <div className="text-sm font-medium">{t('settings.enableVoice', 'Enable Voice Input')}</div>
            <div className="text-xs text-muted-foreground">
              {t('settings.enableVoiceDesc', 'Show microphone button in chat input bar')}
            </div>
          </div>
          <input
            type="checkbox"
            checked={voiceInputEnabled}
            onChange={(e) => setVoiceInputEnabled(e.target.checked)}
            className="accent-primary h-4 w-4"
          />
        </label>
      </section>

      {/* Multi-Login Policy */}
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Monitor className="h-3.5 w-3.5" />
          {t('settings.multiLogin', 'Multi-Login Policy')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('settings.multiLoginDesc', 'Control whether your account can be logged in from multiple devices simultaneously.')}
        </p>
        <div className="space-y-2">
          {([
            { value: 'none' as const, label: t('settings.mlNone', 'Single Session'), desc: t('settings.mlNoneDesc', 'Only one active session allowed. Previous sessions are terminated on new login.') },
            { value: 'hidden' as const, label: t('settings.mlHidden', 'Allow Multiple'), desc: t('settings.mlHiddenDesc', 'Multiple sessions allowed silently. No notification when your account logs in elsewhere.') },
            { value: 'notify' as const, label: t('settings.mlNotify', 'Allow & Notify'), desc: t('settings.mlNotifyDesc', 'Multiple sessions allowed. Dashboard shows how many devices are currently logged in.') },
          ]).map((opt) => (
            <label
              key={opt.value}
              className={cn(
                'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                multiLoginPolicy === opt.value
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-input hover:bg-accent/50',
              )}
            >
              <input
                type="radio"
                name="multiLogin"
                value={opt.value}
                checked={multiLoginPolicy === opt.value}
                onChange={() => setMultiLoginPolicy(opt.value)}
                className="accent-primary h-4 w-4"
              />
              <div>
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Save */}
      {error && (
        <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
      )}
      {saved && (
        <p className="text-sm text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
          {t('settings.saved', 'Profile saved successfully.')}
        </p>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        className={cn(
          'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          saving && 'opacity-50 cursor-wait',
        )}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {t('common.save', 'Save')}
      </button>
    </div>
  );
}
